import {
    GlobalNotificationContext,
    GlobalNotificationStatus,
} from "@nova/component-global-notification"
import { extractApiGatewayError } from "@nova/fetch-api-gateway"
import { ConfirmationModalProps } from "features/modals/WorkspaceConfirmationModalV2"
import { useAppDispatch } from "integrations/redux/hooks"
import useSWRMutation from "swr/mutation"
import { setGlobalNotificationContext } from "user/profile/slice"

const DEFAULT_ERROR_MESSAGE = "Failed to save account details. Please try again."

export const usePilotTrackingConnectedAccountMutation = ({
    key,
    confirm,
    cancel,
    mutate,
    completionGlobalNotificationContext,
    failureGlobalNotificationContext,
}: {
    key: string
    mutate: () => Promise<unknown>
    completionGlobalNotificationContext?: GlobalNotificationContext
    failureGlobalNotificationContext?: GlobalNotificationContext
} & Pick<ConfirmationModalProps, "confirm" | "cancel">) => {
    const dispatch = useAppDispatch()

    const swr = useSWRMutation(key, async () => {
        let context: GlobalNotificationContext = {
            status: GlobalNotificationStatus.error,
            message: DEFAULT_ERROR_MESSAGE,
        }

        try {
            await mutate()
            context = completionGlobalNotificationContext || {
                status: GlobalNotificationStatus.success,
                message: "Account details have been saved securely.",
                timeoutInMs: 3000,
            }

            confirm()
        } catch (error) {
            console.error(error)

            const errorMessage = extractApiGatewayError(error)?.message

            context = failureGlobalNotificationContext || {
                status: GlobalNotificationStatus.error,
                message: errorMessage || DEFAULT_ERROR_MESSAGE,
                timeoutInMs: 5000,
            }

            cancel()
        } finally {
            dispatch(setGlobalNotificationContext(context))
        }
    })

    return {
        trigger: swr.trigger,
        isLoading: swr.isMutating,
    }
}
